import { CommonModule } from "@angular/common";
import { Component, inject, Input, OnDestroy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { catchError, EMPTY, Subject, Subscription, switchMap, takeUntil, timer } from "rxjs";
import { RestService } from "../../../core/services/rest.service";
import { ProcessLogEntry } from "../../common/process-logs-loader/process-log-entry";
import { ProcessLogsLoaderComponent } from "../../common/process-logs-loader/process-logs-loader.component";

@Component({
  selector: "app-generate-test-cases-modal",
  standalone: true,
  imports: [CommonModule, FormsModule, ProcessLogsLoaderComponent],
  templateUrl: "./generate-test-cases-modal.component.html",
  styleUrl: "./generate-test-cases-modal.component.scss",
})
export class GenerateTestCasesModalComponent implements OnDestroy {
  @Input() configs: any = {};

  activeModal = inject(NgbActiveModal);
  private readonly restService = inject(RestService);
  private readonly destroy$ = new Subject<void>();
  private pollSub: Subscription | null = null;

  isActive = false;
  functionalEnabled = false;
  selectedTestType = "";
  nonFunctionalEnabled = false;

  isGenerating = false;
  isProgressError = false;
  progressText = "Generating test cases...";
  progressPercentage = 3;
  errorText = "";
  processLogs: ProcessLogEntry[] = [];

  get canGenerate(): boolean {
    if (this.isGenerating) return false;
    if (this.nonFunctionalEnabled) return true;
    return this.functionalEnabled && !!this.selectedTestType;
  }

  get processStatusLabel(): string {
    if (this.isProgressError) return "Failed";
    if (this.progressPercentage >= 100) return "Completed";
    return "Thinking...";
  }

  get isProcessActive(): boolean {
    return this.isGenerating && !this.isProgressError && this.progressPercentage < 100;
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFunctionalToggle(): void {
    if (this.functionalEnabled) {
      this.nonFunctionalEnabled = false;
      // Auto-select first option when enabling
      if (!this.selectedTestType) {
        this.selectedTestType = "smoke";
      }
    } else {
      this.selectedTestType = "";
    }
  }

  onNonFunctionalToggle(): void {
    if (this.nonFunctionalEnabled) {
      this.functionalEnabled = false;
      this.selectedTestType = "";
    }
  }

  onGenerate(): void {
    if (!this.canGenerate) return;

    const projectId: string = this.configs?.projectId ?? "";
    const configId: string = this.configs?.configId ?? "";
    const selectedStories: any[] = this.configs?.selectedStories ?? [];
    const selectedStoryKeys = selectedStories
      .map((s: any) => s.storyId)
      .filter(Boolean);
    const testCaseType = this.mapTestCaseType();

    if (!projectId || !configId) {
      this.errorText = "Missing project or extraction config. Please extract user stories again.";
      return;
    }
    if (!selectedStoryKeys.length) {
      this.errorText = "Please select at least one user story.";
      return;
    }

    this.isGenerating = true;
    this.isProgressError = false;
    this.errorText = "";
    this.progressText = "Generating test cases...";
    this.progressPercentage = 3;
    this.resetProcessLogs("Starting test case generation...");

    const payload = {
      projectId,
      configId,
      selectedStoryKeys,
      excludedStoryKeys: [],
      selectedAll: false,
      testCaseType,
    };

    this.restService.generateTestCase(payload).pipe(
      catchError((err: any) => {
        this.isGenerating = false;
        this.isProgressError = true;
        this.errorText =
          err?.error?.message || err?.message || "Generation failed. Please try again.";
        this.failProcessLog(this.errorText);
        return EMPTY;
      })
    ).subscribe((res: any) => {
      const versionId: string = res?.data?.versionId ?? "";
      if (!versionId) {
        this.isGenerating = false;
        this.isProgressError = true;
        this.errorText = "No version ID returned from generation API.";
        this.failProcessLog(this.errorText);
        return;
      }
      this.completeProcessLogStep(
        "Generation request accepted.",
        "Extracting paths and generating test cases..."
      );
      this.pollProgress(projectId, versionId, testCaseType);
    });
  }

  private pollProgress(projectId: string, versionId: string, testCaseType: string): void {
    this.pollSub?.unsubscribe();
    this.pollSub = timer(0, 5000).pipe(
      switchMap(() => this.restService.getTestCaseByVersionId(projectId, versionId)),
      takeUntil(this.destroy$),
      catchError((err: any) => {
        this.isGenerating = false;
        this.isProgressError = true;
        this.errorText =
          err?.error?.message || err?.message || "Error tracking generation status.";
        this.failProcessLog(this.errorText);
        return EMPTY;
      })
    ).subscribe((statusRes: any) => {
      const data = statusRes?.data ?? {};
      const status = (data.status ?? "").toUpperCase();
      const baseMessage = data.message ?? "Generating test cases...";

      const totalPaths = Number(data.totalPaths) || 0;
      const processingPaths = Number(data.processingPaths) || 0;

      // Calculate percentage: 0 when paths not yet known, capped at 99 until COMPLETED
      const pct = totalPaths > 0
        ? Math.min(99, Math.round((processingPaths / totalPaths) * 100))
        : 0;
      this.progressPercentage = Math.max(pct, 3); // keep progress bar visually non-zero

      // Always show percentage in the message text
      this.progressText = `${baseMessage} (${pct}%)`;
      this.updateLiveThinking(this.progressText);

      if (status === "COMPLETED" || status === "FAILED" || status === "FAILURE") {
        this.pollSub?.unsubscribe();

        if (status !== "COMPLETED") {
          this.isGenerating = false;
          this.isProgressError = true;
          this.errorText =
            data.error ?? data.message ?? "Test case generation failed.";
          this.failProcessLog(this.errorText);
          return;
        }

        this.progressPercentage = 100;
        this.progressText = data.message ?? "Generation complete!";
        this.completeProcessLogStep(
          "Test case generation completed successfully.",
          undefined,
          true
        );
        setTimeout(() => {
          this.isGenerating = false;
          this.activeModal.close({
            functionalEnabled: this.functionalEnabled,
            testType: this.functionalEnabled ? this.selectedTestType : null,
            nonFunctionalEnabled: this.nonFunctionalEnabled,
            testCaseType,
            versionId,
            testCases: data.testCases ?? [],
          });
        }, 600);
      }
    });
  }

  private mapTestCaseType(): string {
    if (this.nonFunctionalEnabled) return "non_functional";
    if (this.functionalEnabled) {
      if (this.selectedTestType === "sanity") return "functional_sanity";
      if (this.selectedTestType === "regression") return "functional_regression";
      return "functional_smoke";
    }
    return "functional_smoke";
  }

  private resetProcessLogs(thinking: string): void {
    this.processLogs = [{ step: 1, thinking, evaluation: "" }];
  }

  private updateLiveThinking(thinking: string): void {
    if (!this.processLogs.length) {
      this.resetProcessLogs(thinking);
      return;
    }
    const latest = this.processLogs[this.processLogs.length - 1];
    this.processLogs = [
      ...this.processLogs.slice(0, -1),
      { ...latest, thinking },
    ];
  }

  private completeProcessLogStep(
    evaluation: string,
    nextThinking?: string,
    finalize = false
  ): void {
    const completed: ProcessLogEntry[] = this.processLogs.map((log, idx) => {
      if (idx !== this.processLogs.length - 1) return log;
      return {
        ...log,
        evaluation: evaluation || log.evaluation || log.thinking || "",
        thinking: "",
      };
    });

    if (finalize || !nextThinking) {
      this.processLogs = completed;
      return;
    }

    this.processLogs = [
      ...completed,
      { step: completed.length + 1, thinking: nextThinking, evaluation: "" },
    ];
  }

  private failProcessLog(message: string): void {
    if (!this.processLogs.length) {
      this.processLogs = [{ step: 1, thinking: message, evaluation: "" }];
      return;
    }
    const latest = this.processLogs[this.processLogs.length - 1];
    this.processLogs = [
      ...this.processLogs.slice(0, -1),
      { ...latest, thinking: message },
    ];
  }
}
